import type { FunctionComponent } from 'react';

import { nuc } from '../../../utils';
import { DocsLink, icons, IconText, RouterLink } from '../../generic';
import BuildList from '../BuildList';
import useBuildList from '../ProjectBuildsPage/useBuildList';
import type { VCSHost } from '../../../../shared/projectsV2/vcs';
import type { RevisionStatus } from '../dependencies/utils';
import type { ProjectDashboardRevision } from '../projectDashboardTypes';

import BuildCard from './BuildCard';

export type Props = {
  vcsHost: VCSHost;
  defaultBranch: string;
  isCLIBuild: boolean;
  userCanEditProject: boolean;
  revisionLocator: string;
  revisionMessage: string;
  reference: string;
  revisionId: string;
  projectId: string;
  status: Omit<RevisionStatus, 'failing' | 'analyzed'>;
  onProjectChange?: () => void;
  revision: ProjectDashboardRevision;
};

const ProjectSummaryBuildInfo: FunctionComponent<Props> = ({
  vcsHost,
  defaultBranch,
  isCLIBuild,
  revisionLocator,
  revisionMessage,
  userCanEditProject,
  reference,
  revisionId,
  status,
  projectId,
  onProjectChange,
  revision,
}) => {
  const { builds } = useBuildList({
    mode: 'REVISION',
    locator: `${projectId}$${revisionId}`,
  });
  // For GitHub App hybrid projects (imported using the CLI but also connected to GitHub via quick import),
  // we allow builds to be fetched from GitHub. Instead of displaying the typical CLI build card,
  // we show the same UI used for automated build cards.
  const isGithubAppProject = vcsHost === 'github-app';
  const isCLIBuildProject = isCLIBuild && !isGithubAppProject;

  return (
    <div className="row">
      <div className="col-md-8 col-md-offset-2">
        <div className="animated fadeInUp fast">
          <BuildCard
            status={status}
            reference={reference}
            revisionId={revisionId}
            isCLIBuild={isCLIBuildProject}
            defaultBranch={defaultBranch}
            revisionLocator={revisionLocator}
            revisionMessage={revisionMessage}
            userCanEditProject={userCanEditProject}
          />
          <BuildList
            builds={builds}
            onLatestComplete={onProjectChange}
            currentRevision={revision}
          >
            <div>
              {isCLIBuildProject && (
                <div className="card">
                  <h4>Provided Builds</h4>
                  <p>
                    This project is configured with <b>Provided Builds</b>. FOSSA has not received build results for
                    this version yet. Check the status of your build and the FOSSA plugin.
                  </p>
                  &nbsp;
                  <RouterLink className="btn" to={`/projects/${nuc(projectId)}/settings/builds`}>
                    <IconText
                      icon={icons.customize}
                      text="Configure Provided Builds"
                    />
                  </RouterLink>
                  &nbsp;&nbsp;&nbsp;
                  <DocsLink
                    to="provided-builds"
                    className="btn"
                  >
                    <IconText
                      icon={icons.download}
                      text="Download the Plugin"
                    />
                  </DocsLink>
                </div>
              )}
            </div>
          </BuildList>
        </div>
      </div>
    </div>
  );
};

export default ProjectSummaryBuildInfo;
